import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApiDocs, useClientRole, useWorkspace } from '@/api';
import { QueryPending } from '@/components/QueryState';
import { Empty, Loading, PageHeader, Tabs } from '@/components/ui';
import { curlExamples, ERROR_CODES, N8N_STEPS, oneCExample, PROBLEM_EXAMPLE, VERIFY_JS, VERIFY_PHP_1C, VERIFY_PYTHON, WEBHOOK_EVENTS, WEBHOOK_RETRIES, webhookPayloadExample } from '@/lib/apiGuide';
import { SCOPE_LABEL } from '@/lib/apiKeys';
import type { ApiScope } from '@/api/types';
import { baseUrl, groups, opKey, OPS_RU, refsOf, resolve, typeLabel, usedSchemas, type Endpoint, type OpenApiSpec, type Schema } from '@/lib/openapi';

type Tab = 'guide' | 'reference';

/**
 * «Документация API» (MK42-6385): only inside the cabinet, for the owner while the studio keeps
 * the API on (owner 14.10, decision 7). The spec comes from the bff after sign-in and is rendered
 * here — no third-party script (the CSP allows none), no public copy.
 */
export function ApiDocs() {
  const role = useClientRole();
  const { data: ws } = useWorkspace();
  const allowed = role === 'owner' && !!ws?.apiEnabled;
  const spec = useApiDocs(allowed);
  const [tab, setTab] = useState<Tab>('guide');
  const { hash } = useLocation();
  // A link to a section (/settings/api#webhooks): scroll there once the guide is drawn.
  const ready = !!spec.data;
  useEffect(() => {
    if (ready && hash) document.getElementById(hash.slice(1))?.scrollIntoView?.({ block: 'start' });
  }, [ready, hash]);
  const back = (
    <Link to="/settings" className="text-sm text-muted hover:text-ink">
      ← Компания и команда
    </Link>
  );
  if (!ws) return <Loading />;
  if (role !== 'owner') {
    return (
      <>
        <PageHeader title="Документация API" back={back} />
        <Empty title="Документация API — у владельца кабинета">Ключи и подключение систем компании настраивает владелец кабинета.</Empty>
      </>
    );
  }
  if (!ws.apiEnabled) {
    return (
      <>
        <PageHeader title="Документация API" back={back} />
        <Empty title="API для компании не включено">Напишите студии, если хотите подключить 1С, CRM или n8n.</Empty>
      </>
    );
  }
  return (
    <>
      <PageHeader title="Документация API" description="Как подключить 1С, CRM и n8n к кабинету, и все методы API с правами и полями." back={back} />
      <Tabs
        id="api-docs"
        label="Документация API"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'guide', label: 'Как подключить' },
          { value: 'reference', label: 'Справочник' },
        ]}
      />
      <div id="api-docs-panel" role="tabpanel" aria-labelledby={`api-docs-tab-${tab}`} className="max-w-4xl">
        {!spec.data ? <QueryPending query={spec} /> : tab === 'guide' ? <Guide spec={spec.data} /> : <Reference spec={spec.data} />}
      </div>
    </>
  );
}

function Code({ children, label }: { children: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="overflow-x-auto whitespace-pre rounded bg-sunken p-3 pr-20 font-mono text-xs leading-relaxed">
        <code>{children}</code>
      </pre>
      <button
        type="button"
        className="btn-ghost absolute right-1.5 top-1.5 h-6 px-2 text-xs"
        aria-label={`Скопировать: ${label}`}
        onClick={() => {
          void navigator.clipboard?.writeText(children).then(() => setCopied(true));
        }}
      >
        {copied ? 'Скопировано' : 'Копировать'}
      </button>
    </div>
  );
}

function Block({ title, id, children }: { title: string; id: string; children: ReactNode }) {
  return (
    <section id={id} className="mt-10 first:mt-0">
      <h2 className="mb-3 text-md font-semibold tracking-tight">{title}</h2>
      <div className="space-y-3 text-sm">{children}</div>
    </section>
  );
}

const mono = 'font-mono text-xs';

export function Guide({ spec }: { spec: OpenApiSpec }) {
  const base = baseUrl(spec);
  return (
    <div>
      <Block title="Адрес и ключ" id="start">
        <p>
          Адрес API вашего кабинета: <code className={mono}>{base}</code>. Ключ выпускает владелец в разделе{' '}
          <Link className="underline underline-offset-2" to="/settings">
            «Интеграции»
          </Link>
          ; он показывается один раз. Каждый запрос — с заголовком <code className={mono}>Authorization: Bearer m42_…</code>; в адресе ключ не принимается.
        </p>
        <p className="text-muted">
          Ключ видит только данные вашей компании и только то, что разрешают его права. Ключ одного контура не работает в другом. Неизвестные поля в ответах игнорируйте: в v1 поля только добавляются.
        </p>
      </Block>

      <Block title="Примеры запросов" id="curl">
        <p className="text-muted">
          Ключ — в переменной окружения <code className={mono}>M42_KEY</code>. Вместо <code className={mono}>&lt;invoiceId&gt;</code> и других угловых скобок — id из ответа списка.
        </p>
        {curlExamples(base).map((e) => (
          <div key={e.id} className="pt-2">
            <h3 className="font-medium">{e.title}</h3>
            {e.note && <p className="mb-2 mt-0.5 text-muted">{e.note}</p>}
            <Code label={e.title}>{e.code}</Code>
          </div>
        ))}
      </Block>

      <Block title="1С: счета в бухгалтерию" id="1c">
        <p className="text-muted">Ключ с набором «Бухгалтерия». Раз в день или по кнопке: забрать счета страницами, PDF — по id, после оплаты переводом — отметить её (POST с Idempotency-Key).</p>
        <Code label="1С">{oneCExample(base)}</Code>
      </Block>

      <Block title="n8n и CRM: идеи и запросы" id="n8n">
        <ol className="list-decimal space-y-1.5 pl-5">
          {N8N_STEPS.map((s) => (
            <li key={s}>{s.replace('{base}', base)}</li>
          ))}
        </ol>
      </Block>

      <Block title="Вебхуки" id="webhooks">
        <p>
          Вместо опроса — события: портал сам отправит POST на адрес вашего сервера. Адрес добавляет владелец в{' '}
          <Link className="underline underline-offset-2" to="/settings">
            «Интеграции»
          </Link>
          : только <code className={mono}>https://</code>, порт 443 или 8443, без логина в адресе, без перенаправлений. Секрет подписи показывается один раз; точка включается, когда «Проверить» получит ответ 2xx.
        </p>
        <table className="w-full text-left text-sm">
          <thead className="text-muted">
            <tr>
              <th className="py-1.5 pr-3 font-normal">Событие</th>
              <th className="py-1.5 pr-3 font-normal">Что в data</th>
              <th className="py-1.5 font-normal">Право для дозапроса</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {WEBHOOK_EVENTS.map((e) => (
              <tr key={e.type}>
                <td className={`py-1.5 pr-3 ${mono}`}>{e.type}</td>
                <td className="py-1.5 pr-3">{e.what}</td>
                <td className={`py-1.5 ${mono}`}>{e.scope}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h3 className="pt-2 font-medium">Что приходит</h3>
        <p className="text-muted">
          «Тонкое» событие: только id, номера и статусы, без сумм и имён. Подробности заберите ключом API по <code className={mono}>links.self</code>. <code className={mono}>webhook-id</code> одинаков во всех повторах одного события: по нему отбрасывайте дубли.
          Порядок событий не гарантируется.
        </p>
        <Code label="Пример события">{webhookPayloadExample(base)}</Code>
        <h3 className="pt-2 font-medium">Проверка подписи</h3>
        <p className="text-muted">
          Standard Webhooks: HMAC-SHA256 секретом от <code className={mono}>webhook-id.webhook-timestamp.тело</code>, в base64, сравнить с каждой <code className={mono}>v1,…</code> из <code className={mono}>webhook-signature</code>. Тело берите как пришло, до разбора JSON. После смены секрета 24 часа приходят
          две подписи — подходит любая.
        </p>
        <Code label="Проверка на Python">{VERIFY_PYTHON}</Code>
        <Code label="Проверка на JavaScript">{VERIFY_JS}</Code>
        <Code label="Проверка на PHP и шаги для 1С">{VERIFY_PHP_1C}</Code>
        <h3 className="pt-2 font-medium">Повторы и выключение</h3>
        <p>
          Ответ 2xx за 10 секунд — доставлено. Сетевая ошибка, <code className={mono}>408</code>, <code className={mono}>429</code>, <code className={mono}>5xx</code> — повтор через {WEBHOOK_RETRIES} (7 попыток, около 31 часа; <code className={mono}>Retry-After</code> учитывается). Другие{' '}
          <code className={mono}>4xx</code> и перенаправления — без повторов.
        </p>
        <p>
          Если 3 дня подряд нет ни одной успешной доставки, вебхук выключается, владельцу кабинета приходит уведомление. Почините сервер, включите вебхук и нажмите «Повторить неудачные за период». Журнал доставок хранится 30 дней.
        </p>
      </Block>

      <Block title="Страницы" id="pages">
        <p>
          Списки отдаются страницами: <code className={mono}>?limit=50</code> (до 200) → <code className={mono}>{'{"data": [...], "nextCursor": "…"}'}</code>. Передавайте <code className={mono}>nextCursor</code> как{' '}
          <code className={mono}>cursor</code>, пока он не станет <code className={mono}>null</code>. Для синхронизации — <code className={mono}>updatedSince</code> (ISO 8601, UTC) на идеях, задачах и отчётах.
        </p>
      </Block>

      <Block title="Лимиты" id="limits">
        <p>На ключ: чтение — 120 запросов в минуту и 20 000 в сутки, запись — 20 в минуту и 500 в сутки. Задачи опрашивайте не чаще раза в 5 минут.</p>
        <p>
          В ответах — <code className={mono}>RateLimit-Limit</code>, <code className={mono}>RateLimit-Remaining</code>, <code className={mono}>RateLimit-Reset</code>. Сверх лимита — <code className={mono}>429</code> с заголовком{' '}
          <code className={mono}>Retry-After</code>: подождите столько секунд и повторите.
        </p>
      </Block>

      <Block title="Ошибки" id="errors">
        <p>
          Ошибка — <code className={mono}>application/problem+json</code>: <code className={mono}>title</code> для людей, <code className={mono}>code</code> для программы, <code className={mono}>requestId</code> — назовите его студии, если что-то не так.
        </p>
        <Code label="Пример ошибки">{PROBLEM_EXAMPLE}</Code>
        <table className="w-full text-left text-sm">
          <thead className="text-muted">
            <tr>
              <th className="py-1.5 pr-3 font-normal">Статус</th>
              <th className="py-1.5 pr-3 font-normal">code</th>
              <th className="py-1.5 font-normal">Что значит</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {ERROR_CODES.map((e) => (
              <tr key={e.code}>
                <td className="num py-1.5 pr-3">{e.status}</td>
                <td className={`py-1.5 pr-3 ${mono}`}>{e.code}</td>
                <td className="py-1.5">{e.what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Block>
    </div>
  );
}

const scopeLabel = (s: string) => SCOPE_LABEL[s as ApiScope] ?? s;

function TypeRef({ schema }: { schema: Schema | undefined }) {
  const refs = refsOf(schema);
  return (
    <span className={mono}>
      {typeLabel(schema)}
      {refs.length > 0 &&
        refs.map((r) => (
          <a key={r} href={`#schema-${r}`} className="ml-1.5 text-muted underline underline-offset-2" aria-label={`Схема ${r}`}>
            ↓
          </a>
        ))}
    </span>
  );
}

function EndpointView({ e }: { e: Endpoint }) {
  const ru = OPS_RU[opKey(e)];
  const params = (e.op.parameters ?? []).filter((p) => p.in !== 'cookie');
  const body = e.op.requestBody?.content?.['application/json']?.schema;
  const responses = Object.entries(e.op.responses ?? {}).filter(([code]) => code !== '422');
  return (
    <article id={e.id} className="border-t border-line py-5 first:border-t-0" aria-label={opKey(e)}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="w-12 shrink-0 font-mono text-xs font-semibold uppercase">{e.method}</span>
        <code className="font-mono text-sm">{e.path}</code>
        <span className="text-sm text-muted">{ru?.title ?? e.op.summary}</span>
      </div>
      <div className="mt-2 space-y-3 pl-0 text-sm sm:pl-[3.75rem]">
        {(ru?.note || e.op.description) && <p className="text-muted">{ru?.note ?? e.op.description}</p>}
        <p>
          <span className="text-muted">Право: </span>
          {e.op['x-scopes']?.length ? (
            e.op['x-scopes'].map((s) => (
              <span key={s} className="mr-2">
                <code className={mono}>{s}</code> <span className="text-muted">({scopeLabel(s)})</span>
              </span>
            ))
          ) : (
            <span className="text-muted">не нужно</span>
          )}
          {e.method !== 'get' && (
            <span className="ml-1 text-muted">
              · заголовок <code className={mono}>Idempotency-Key</code> обязателен
            </span>
          )}
        </p>
        {params.length > 0 && (
          <table className="w-full text-left">
            <thead className="text-muted">
              <tr>
                <th className="py-1 pr-3 font-normal">Параметр</th>
                <th className="py-1 pr-3 font-normal">Где</th>
                <th className="py-1 pr-3 font-normal">Тип</th>
                <th className="py-1 font-normal">Описание</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {params.map((p) => (
                <tr key={`${p.in}-${p.name}`}>
                  <td className={`py-1 pr-3 ${mono}`}>
                    {p.name}
                    {p.required && <span className="text-muted"> *</span>}
                  </td>
                  <td className="py-1 pr-3 text-muted">{{ path: 'путь', query: 'запрос', header: 'заголовок', cookie: 'cookie' }[p.in]}</td>
                  <td className="py-1 pr-3">
                    <TypeRef schema={p.schema} />
                  </td>
                  <td className="py-1 text-muted">{p.description ?? p.schema?.description ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {body && (
          <p>
            <span className="text-muted">Тело (JSON): </span>
            <TypeRef schema={body} />
          </p>
        )}
        {responses.length > 0 && (
          <ul className="space-y-0.5">
            {responses.map(([code, r]) => {
              const [mime, body] = Object.entries(r.content ?? {})[0] ?? [];
              const typed = mime === 'application/json' && body?.schema && Object.keys(body.schema).length > 0;
              return (
                <li key={code}>
                  <span className="num mr-2">{code}</span>
                  {typed ? <TypeRef schema={body.schema} /> : <span className={mono}>{mime ?? ''}</span>}
                  {!typed && r.description && r.description !== 'Successful Response' && <span className="ml-2 text-muted">{r.description}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </article>
  );
}

function SchemaView({ spec, name }: { spec: OpenApiSpec; name: string }) {
  const s = resolve(spec, { $ref: `#/components/schemas/${name}` });
  if (!s) return null;
  const required = new Set(s.required ?? []);
  const props = Object.entries(s.properties ?? {});
  return (
    <article id={`schema-${name}`} className="border-t border-line py-4 first:border-t-0" aria-label={`Схема ${name}`}>
      <h3 className="font-mono text-sm font-semibold">{name}</h3>
      {s.description && <p className="mt-1 text-sm text-muted">{s.description}</p>}
      {props.length > 0 && (
        <table className="mt-2 w-full text-left text-sm">
          <tbody className="divide-y divide-line">
            {props.map(([field, f]) => (
              <tr key={field}>
                <td className={`w-1/4 py-1 pr-3 align-top ${mono}`}>
                  {field}
                  {required.has(field) && <span className="text-muted"> *</span>}
                </td>
                <td className="w-1/3 py-1 pr-3 align-top">
                  <TypeRef schema={f} />
                </td>
                <td className="py-1 align-top text-muted">{f.description ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
}

export function Reference({ spec }: { spec: OpenApiSpec }) {
  const gs = groups(spec);
  const schemas = usedSchemas(spec);
  return (
    <div>
      <p className="mb-6 text-sm text-muted">
        База: <code className={mono}>{baseUrl(spec)}</code> · версия {spec.info.version} · <span className={mono}>*</span> — обязательное поле. Ошибки — problem+json (см. «Как подключить»).
      </p>
      <nav aria-label="Разделы API" className="mb-8 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {gs.map((g) => (
          <a key={g.tag} href={`#group-${g.tag}`} className="text-muted underline-offset-2 hover:text-ink hover:underline">
            {g.label}
          </a>
        ))}
        {schemas.length > 0 && (
          <a href="#schemas" className="text-muted underline-offset-2 hover:text-ink hover:underline">
            Схемы
          </a>
        )}
      </nav>
      {gs.map((g) => (
        <section key={g.tag} id={`group-${g.tag}`} className="mb-10">
          <h2 className="mb-1 text-md font-semibold tracking-tight">{g.label}</h2>
          {g.endpoints.map((e) => (
            <EndpointView key={e.id} e={e} />
          ))}
        </section>
      ))}
      {schemas.length > 0 && (
        <section id="schemas">
          <h2 className="mb-1 text-md font-semibold tracking-tight">Схемы</h2>
          {schemas.map((n) => (
            <SchemaView key={n} spec={spec} name={n} />
          ))}
        </section>
      )}
    </div>
  );
}
